import { Container, Grid, Box, Hidden } from "@mui/material"
import { Icon } from "@mdi/react"
import { mdiCart } from "@mdi/js"

import Button from "components/Buttons/Button"
import Delivery from "containers/ProductDetailPage/ProductHeader/Delivery"
import Price, { TaxString } from "components/Price"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"

import useTranslation from "hooks/useTranslation"

import { makeStyles } from "makeStyles"
import { flatFeesByCurrencyCode } from "@secureo/common/config/shippingZonesAndCosts"

import { DeliveryOption } from "@secureo/common/typings/Product"
import { ProductDetailPageReducerState } from "@secureo/common/reducers/productDetailPageReducer"

const useStyles = makeStyles()({
	box: {
		borderRadius: "5px",
		boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
		background: "rgba(247, 250, 250, 0.7)",
	},
	price: {
		padding: 0,
		margin: "0.25rem 0",
		fontSize: "150%",
		color: "#1B5C71",
	},
	oldPrice: {
		fontSize: "12px",
		textDecoration: "line-through",
		color: "lightgrey",
	},
	mobilePriceAndAddToCart: {
		position: "fixed",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		bottom: 0,
		left: 0,
		right: "0",
		backgroundColor: "#f7fafa",
		boxShadow: "0px -2px 5px 0px rgba(0,0,0,0.15)",
		zIndex: 999,
		padding: 10,
	},
})

const PriceAndDelivery = ({
	addToCart,
	setQuantity,
	reducerState,
	deliveryOptionsBySku,
	language,
}: Props) => {
	const { classes } = useStyles()
	const { t } = useTranslation("product")

	const {
		isLoading,
		productType,
		quantity,
		singleGrossPriceCents,
		singleGrossMsrpPriceCents,
		additionalDeliveryTimeWorkDays = 0,
		countryCode,
		currencyCode,
		attributes: {
			shippingOriginationCountryCodesByDestinationCountryCode,
			isHaulageDelivery,
			weightKg,
			deliveryOptionsByDestinationCountryCode,
			deliveryTimeWorkDaysByShopCountryCode,
		},
	} = reducerState

	const deliveryTimeWorkDays =
		(deliveryTimeWorkDaysByShopCountryCode?.[countryCode] ?? 0) +
		(additionalDeliveryTimeWorkDays ?? 0)

	return (
		<Box my={3} p={2} className={classes.box}>
			<Grid container spacing={2}>
				<Hidden mdUp>
					<Container>
						<div className={classes.mobilePriceAndAddToCart}>
							<div>
								<span className={classes.oldPrice}>
									{singleGrossMsrpPriceCents && (
										<Price priceInEuroCents={singleGrossMsrpPriceCents} />
									)}
								</span>
								<span className="small" style={{ marginLeft: "5px" }}>
									<TaxString />
								</span>
								<div className={classes.price}>
									<Price priceInEuroCents={singleGrossPriceCents} />
								</div>
							</div>
							<div>
								{
									<>
										<Box mr={1} mb={1}>
											<ChangeAmountButton
												quantity={quantity}
												onChange={setQuantity}
												disabled={isLoading}
											/>
										</Box>
										<Button
											color="cart"
											onClick={addToCart}
											style={{ width: "100%" }}
										>
											{t("addToCart")}
										</Button>
									</>
								}
							</div>
						</div>
					</Container>
				</Hidden>
				<Grid item xs={12} sm={12} lg={6}>
					<Box>
						<div
							style={{
								textDecoration: "line-through",
								color: "lightgrey",
							}}
						>
							{singleGrossMsrpPriceCents && (
								<Price priceInEuroCents={singleGrossMsrpPriceCents} />
							)}
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
							}}
						>
							<div className={classes.price}>
								<strong>
									<Price priceInEuroCents={singleGrossPriceCents} />
								</strong>
							</div>
							<Box ml={1} className="small">
								<TaxString />
							</Box>
						</div>
						<Box
							mt={1}
							style={{
								display: "flex",
								alignItems: "center",
							}}
						>
							{
								<>
									<Box mr={1}>
										<ChangeAmountButton
											quantity={quantity}
											onChange={setQuantity}
											disabled={isLoading}
										/>
									</Box>
									<Button
										color="cart"
										onClick={addToCart}
										style={{ maxWidth: "300px" }}
										disabled={isLoading}
									>
										<div style={{ marginRight: "5px" }}>{t("addToCart")}</div>
										<Icon path={mdiCart} size={0.7} />
									</Button>
								</>
							}
						</Box>
					</Box>
				</Grid>
				{deliveryTimeWorkDays > 0 && (
					<Grid item xs={12} sm={12} lg={6}>
						<Box>
							<Delivery
								deliveryTimeWorkdays={deliveryTimeWorkDays}
								isFreeShipping={
									singleGrossPriceCents >
									flatFeesByCurrencyCode[currencyCode]
										.flatFeeForSmallAmountThresholdGrossCents
								}
								totalPriceOfProduct={singleGrossPriceCents}
								hasAssemblyService={productType === "Safe"}
								isHaulageDelivery={isHaulageDelivery}
								deliveryOptions={deliveryOptionsByDestinationCountryCode}
								deliveryOptionsBySku={deliveryOptionsBySku}
								shippingOriginationCountryCodesByDestinationCountryCode={
									shippingOriginationCountryCodesByDestinationCountryCode
								}
								weightKg={weightKg}
								countryCode={countryCode}
								language={language}
								currencyCode={currencyCode}
							/>
						</Box>
					</Grid>
				)}
			</Grid>
		</Box>
	)
}

interface Props {
	reducerState: ProductDetailPageReducerState
	setQuantity: (quantity: number) => void
	addToCart: () => void
	deliveryOptionsBySku: {
		[sku: string]: DeliveryOption
	}
	language: string
}

export default PriceAndDelivery
